import React, { useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, ResponsiveContainer, Tooltip } from 'recharts';
import { AlertTriangle, Search, Tag as TagIcon } from 'lucide-react';
import config from '../config';
import authService from '../services/authService';
import { AppContext } from '../App';
import { TagChip, FilterChip, CustomerTagDialog } from './ui/CustomerTags';

// Pseudo-tag for "has no tags at all" — the filter that answers "who have I not
// categorised yet?", which no real tag can express.
const UNTAGGED = '__untagged__';

const RANGE_OPTIONS = [
  { value: 7, label: 'Last 7 days' },
  { value: 14, label: 'Last 14 days' },
  { value: 30, label: 'Last 30 days' }
];

// Narrow the blown-up Intl formatter to a compact "3 min ago" / "yesterday" style
// so it fits in a table cell without dominating the row.
function relativeTime(iso) {
  if (!iso) return '—';
  const then = new Date(iso);
  const now = new Date();
  const seconds = Math.floor((now - then) / 1000);
  if (Number.isNaN(seconds) || seconds < 0) return new Date(iso).toLocaleString();
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  return then.toLocaleDateString();
}

function Sparkline({ data }) {
  // Flat zero-lines render as a single pixel in recharts with dot={false}; make
  // a zero line visible by giving it a stable mid-value so it looks intentional.
  const hasActivity = data.some((d) => d.units > 0);
  const chartData = hasActivity ? data : data.map((d) => ({ ...d, units: 0 }));

  return (
    <div className="w-32 h-8">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
          <Line
            type="monotone"
            dataKey="units"
            stroke={hasActivity ? '#818cf8' : '#475569'}
            strokeWidth={1.5}
            dot={false}
            isAnimationActive={false}
          />
          <Tooltip
            cursor={false}
            contentStyle={{
              background: '#0f172a',
              border: '1px solid #334155',
              borderRadius: '6px',
              color: '#e2e8f0',
              fontSize: '0.75rem',
              padding: '0.25rem 0.5rem'
            }}
            labelStyle={{ color: '#94a3b8' }}
            formatter={(value) => [`${value} units`, null]}
            labelFormatter={(label) => label}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

const CustomerOverview = () => {
  const { selectedTeam } = useContext(AppContext);
  const navigate = useNavigate();
  const teamId = selectedTeam?.teamId;

  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastRefreshed, setLastRefreshed] = useState(null);

  // Tag vocabulary for this vendor team. Fetched separately from the overview
  // because a newly created tag has no assignments yet, so it appears nowhere
  // in the overview rows — but the assign dialog still has to offer it.
  const [allTags, setAllTags] = useState([]);
  const [search, setSearch] = useState('');
  const [tagFilters, setTagFilters] = useState({}); // tagId -> 'include' | 'exclude'
  const [hideDormant, setHideDormant] = useState(false);
  const [tagDialogFor, setTagDialogFor] = useState(null);

  // 150ms delayed spinner — matches project convention so fast responses don't
  // flash a loading indicator before the table renders.
  useEffect(() => {
    let timer;
    if (loading) timer = setTimeout(() => setShowLoading(true), 150);
    else setShowLoading(false);
    return () => clearTimeout(timer);
  }, [loading]);

  const fetchOverview = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    setError('');
    try {
      const url = `${config.apiBaseUrl}/api/reporting/customer-overview?vendorTeamId=${encodeURIComponent(teamId)}&days=${days}`;
      const res = await fetch(url, { headers: { ...authService.getAuthHeaders() } });
      const body = await res.json();
      if (!res.ok || !body.success) {
        throw new Error(body?.error || `Failed to load overview (HTTP ${res.status})`);
      }
      setData(body);
      setLastRefreshed(new Date());
    } catch (err) {
      console.error('Failed to load customer overview:', err);
      setError(err.message || 'Failed to load overview');
    } finally {
      setLoading(false);
    }
  }, [teamId, days]);

  const fetchTags = useCallback(async () => {
    if (!teamId) return;
    try {
      const url = `${config.apiBaseUrl}/api/customer-tags?vendorTeamId=${encodeURIComponent(teamId)}`;
      const res = await fetch(url, { headers: { ...authService.getAuthHeaders() } });
      const body = await res.json();
      if (!res.ok || !body.success) return;
      setAllTags(body.tags || []);
    } catch (err) {
      // Tags are an aid to reading the table, not the table itself — a failure
      // here must not take the whole page down.
      console.error('Failed to load customer tags:', err);
    }
  }, [teamId]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  // Switching teams must not carry the previous team's filters over — the tag
  // ids are meaningless in another vendor's vocabulary.
  useEffect(() => {
    setTagFilters({});
    setSearch('');
    setHideDormant(false);
  }, [teamId]);

  const cycleTagFilter = (tagId) => {
    setTagFilters((prev) => {
      const next = { ...prev };
      if (!next[tagId]) next[tagId] = 'include';
      else if (next[tagId] === 'include') next[tagId] = 'exclude';
      else delete next[tagId];
      return next;
    });
  };

  // Applied optimistically after the dialog saves, so the row updates without
  // re-fetching the whole overview.
  const handleTagsSaved = (customerProxyTeamId, tags) => {
    setData((prev) =>
      prev
        ? {
            ...prev,
            customers: prev.customers.map((c) =>
              c.customerProxyTeamId === customerProxyTeamId ? { ...c, tags } : c
            )
          }
        : prev
    );
    fetchTags(); // refresh assignment counts on the filter chips
  };

  const sortedCustomers = useMemo(() => {
    if (!data?.customers) return [];

    const includes = Object.keys(tagFilters).filter((k) => tagFilters[k] === 'include');
    const excludes = Object.keys(tagFilters).filter((k) => tagFilters[k] === 'exclude');
    const needle = search.trim().toLowerCase();

    return data.customers.filter((c) => {
      if (needle) {
        const haystack = `${c.customerName || ''} ${c.customerContactEmail || ''}`.toLowerCase();
        if (!haystack.includes(needle)) return false;
      }

      // Lifetime, not the selected window: a customer who burned 50k units last
      // month is dormant-looking in a 7-day view but is not dead weight.
      if (hideDormant && (c.lifetimeUnits ?? 0) === 0) return false;

      const tagIds = (c.tags || []).map((t) => t.id);

      if (excludes.length) {
        if (excludes.includes(UNTAGGED) && tagIds.length === 0) return false;
        if (tagIds.some((id) => excludes.includes(id))) return false;
      }

      if (includes.length) {
        // OR within includes: chips widen the selection rather than narrowing it
        // to an intersection, which is almost never what you want with 2 chips.
        const wantsUntagged = includes.includes(UNTAGGED) && tagIds.length === 0;
        if (!wantsUntagged && !tagIds.some((id) => includes.includes(id))) return false;
      }

      return true;
    });
  }, [data, tagFilters, search, hideDormant]);

  const untaggedCount = useMemo(
    () => (data?.customers || []).filter((c) => (c.tags || []).length === 0).length,
    [data]
  );

  const filtersActive =
    Boolean(search.trim()) || hideDormant || Object.keys(tagFilters).length > 0;
  const totalCustomers = data?.customers?.length ?? 0;

  const handleRowClick = (customer) => {
    // Deep-link into the Consumption Dashboard pre-selected on this customer.
    // The dashboard reads `customerId` from the query string on mount.
    navigate(
      `/consumption?customerId=${encodeURIComponent(customer.customerProxyTeamId)}`
    );
  };

  if (!teamId) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-slate-400">
        Select a team to see its customer activity.
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-slate-100 mb-1">Customer Overview</h2>
          <p className="text-sm text-slate-400 max-w-2xl">
            Birds-eye view of every customer under <strong>{selectedTeam.teamName}</strong>.
            Click a row to drill into their full usage history.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="range" className="text-sm text-slate-400">
            Range:
          </label>
          <select
            id="range"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="bg-slate-900 border border-slate-700 text-slate-100 text-sm rounded px-2 py-1"
          >
            {RANGE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={fetchOverview}
            disabled={loading}
            className="bg-slate-700 hover:bg-slate-600 text-slate-200 text-sm px-3 py-1 rounded transition disabled:opacity-50"
          >
            Refresh
          </button>
          {lastRefreshed && (
            <span
              className="text-xs text-slate-500"
              title={lastRefreshed.toLocaleString()}
            >
              as of{' '}
              {lastRefreshed.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-950/30 border border-red-800/50 text-red-300 px-4 py-3 rounded-lg mb-4" role="alert">
          <strong className="font-semibold">Error:</strong>
          <span className="ml-2">{error}</span>
        </div>
      )}

      {totalCustomers > 0 && (
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-4 mb-4">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <div className="relative flex-1 min-w-[16rem]">
              <Search
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none"
              />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name or email…"
                aria-label="Search customers"
                className="w-full bg-slate-900 border border-slate-700 text-slate-100 text-sm rounded pl-8 pr-2 py-1.5 focus:outline-none focus:border-slate-500"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-400 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={hideDormant}
                onChange={(e) => setHideDormant(e.target.checked)}
                className="rounded border-slate-600 bg-slate-900 text-indigo-500 focus:ring-0"
              />
              Hide never-used
            </label>
            {filtersActive && (
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  setTagFilters({});
                  setHideDormant(false);
                }}
                className="text-xs text-slate-400 hover:text-slate-200 underline"
              >
                Clear filters
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {allTags.length === 0 ? (
              <p className="text-xs text-slate-500">
                No tags yet — use the tag button on any row to group customers by sector,
                lifecycle or region.
              </p>
            ) : (
              <>
                {allTags.map((tag) => (
                  <FilterChip
                    key={tag.id}
                    label={tag.name}
                    count={tag.customerCount}
                    state={tagFilters[tag.id] || 'off'}
                    onClick={() => cycleTagFilter(tag.id)}
                  />
                ))}
                {untaggedCount > 0 && (
                  <FilterChip
                    label="untagged"
                    count={untaggedCount}
                    state={tagFilters[UNTAGGED] || 'off'}
                    onClick={() => cycleTagFilter(UNTAGGED)}
                  />
                )}
              </>
            )}
          </div>

          {allTags.length > 0 && (
            <p className="text-xs text-slate-500 mt-2">
              Click a tag to include it, again to exclude it, again to clear.
            </p>
          )}
        </div>
      )}

      {filtersActive && (
        <p className="text-sm text-slate-400 mb-3">
          Showing <strong className="text-slate-200">{sortedCustomers.length}</strong> of{' '}
          {totalCustomers} customers.
        </p>
      )}

      {loading && showLoading ? (
        <div className="flex items-center text-slate-400">
          <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mr-3" />
          Loading customer activity…
        </div>
      ) : sortedCustomers.length === 0 && !loading ? (
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-6 text-center">
          <AlertTriangle className="w-10 h-10 text-slate-500 mx-auto mb-3" />
          {totalCustomers === 0 ? (
            <>
              <h2 className="text-base font-semibold mb-1 text-slate-100">No customers yet</h2>
              <p className="text-slate-400 text-sm">
                Once you create a customer relationship and issue keys, they'll show up here.
              </p>
            </>
          ) : (
            <>
              <h2 className="text-base font-semibold mb-1 text-slate-100">
                No customers match these filters
              </h2>
              <p className="text-slate-400 text-sm">
                {totalCustomers} customers are hidden by the current search or tag filters.
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="bg-slate-800/50 border border-slate-700 overflow-x-auto sm:rounded-lg">
          <table className="min-w-full divide-y divide-slate-700">
            <thead className="bg-slate-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Tags
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Units ({days}d)
                </th>
                <th
                  className="px-6 py-3 text-right text-xs font-medium text-slate-300 uppercase tracking-wider"
                  title="Total units across all of this customer's keys, for all time"
                >
                  Lifetime
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Trend
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Last activity
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Keys
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Products
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {sortedCustomers.map((c) => (
                <tr
                  key={c.customerProxyTeamId}
                  className="hover:bg-slate-800 cursor-pointer"
                  onClick={() => handleRowClick(c)}
                >
                  <td className="px-6 py-3 whitespace-nowrap">
                    <div className="text-sm text-slate-100">{c.customerName}</div>
                    {c.customerContactEmail && (
                      <div className="text-xs text-slate-500">{c.customerContactEmail}</div>
                    )}
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex flex-wrap items-center gap-1 min-w-[11rem] max-w-[16rem]">
                      {(c.tags || []).map((tag) => (
                        <TagChip key={tag.id} tag={tag} />
                      ))}
                      <button
                        type="button"
                        aria-label={`Edit tags for ${c.customerName}`}
                        title="Edit tags"
                        // The row navigates to the consumption dashboard; tagging
                        // must not trigger that.
                        onClick={(e) => {
                          e.stopPropagation();
                          setTagDialogFor(c);
                        }}
                        className="inline-flex items-center p-1 rounded text-slate-500 hover:text-indigo-300 hover:bg-slate-700/60 transition"
                      >
                        <TagIcon size={13} />
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-right text-sm text-slate-100 font-mono">
                    {c.totalUnits.toLocaleString()}
                  </td>
                  <td
                    className={`px-6 py-3 whitespace-nowrap text-right text-sm font-mono ${
                      (c.lifetimeUnits ?? 0) === 0 ? 'text-slate-600' : 'text-slate-400'
                    }`}
                  >
                    {(c.lifetimeUnits ?? 0).toLocaleString()}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap">
                    <Sparkline data={c.dailyUnits} />
                  </td>
                  <td
                    className="px-6 py-3 whitespace-nowrap text-sm text-slate-400"
                    title={c.lastActivityAt ? new Date(c.lastActivityAt).toLocaleString() : ''}
                  >
                    {relativeTime(c.lastActivityAt)}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-right text-sm text-slate-400">
                    {c.keyCount}
                  </td>
                  {/* max-width on a <td> is ignored in auto table layout, so the
                      cap lives on an inner block. Wrapping (rather than nowrap +
                      ellipsis) lets this column give way when the row is tight —
                      without it the table renders wider than its container and
                      product names are clipped mid-word at the right edge. */}
                  <td className="px-6 py-3 text-sm text-slate-400">
                    <div
                      className="max-w-[14rem] break-words"
                      title={c.productNames.length ? c.productNames.join(', ') : ''}
                    >
                      {c.productNames.length ? c.productNames.join(', ') : '—'}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tagDialogFor && (
        <CustomerTagDialog
          customer={tagDialogFor}
          allTags={allTags}
          vendorTeamId={teamId}
          onClose={() => setTagDialogFor(null)}
          onSaved={handleTagsSaved}
        />
      )}
    </div>
  );
};

export default CustomerOverview;
